import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { Boards } from '@air/api';
import { type Board } from '@air/api/types';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { editBoardSuccessAction } from '~/store/centralizedBoard/actions';
import { useUpdateBoardInAllLists } from '~/utils/mutateUtils/AllBoards';

const DEFAULT_COVER_DIMENSION = 100;

export type UsePrivateBoardsSetBoardCoverMutationParams = Parameters<typeof Boards.setCoverImage>[0] & {
  thumbnailUrl?: string;
  board?: Board;
};

export const usePrivateBoardsSetBoardCoverMutation = () => {
  const dispatch = useDispatch();
  const { updateBoardInAllLists } = useUpdateBoardInAllLists();

  return useMutation({
    mutationFn: async (params: UsePrivateBoardsSetBoardCoverMutationParams) => {
      return callAirApi(() =>
        Boards.setCoverImage({
          boardId: params.boardId,
          workspaceId: params.workspaceId,
          clipId: params.clipId,
          region: params.region,
        }),
      );
    },

    onMutate: ({ board, clipId, region, thumbnailUrl }) => {
      // @TODO - We should update this mutation hook to not rely on passing `board` as a parameter <https://linear.app/air/issue/COR-1406/remove-board-as-a-required-parameters-from-board-cover-mutation-hooks>
      if (board && clipId) {
        const updatedBoard: Board = {
          ...board,
          coverImage: {
            clipId,
            region: {
              // @TODO: look into inconsistent types between set/get cover image https://linear.app/air/issue/COR-956/inconsistent-types-w-cover-image-getset
              top: region.top ?? 0,
              left: region.left ?? 0,
              width: region.width ?? DEFAULT_COVER_DIMENSION,
              height: region.height ?? DEFAULT_COVER_DIMENSION,
            },
            url: thumbnailUrl ?? board.coverImage?.url ?? '',
          },
        };

        updateBoardInAllLists(updatedBoard);
        dispatch(editBoardSuccessAction({ board: updatedBoard }));
      }
    },

    onError: (error, variables) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to set board cover',
        metadata: {
          key: 'data',
          data: variables,
        },
      });
    },
  });
};
